/**
 * Global cart Web Component.
 *
 * <enroute-cart> owns all cart state and mutations. window.__enrouteApp is
 * retained as an alias while callers migrate to window.enrouteCart.
 */
(function () {
  if (customElements.get("enroute-cart")) return;

  class Cart extends HTMLElement {
    constructor() {
      super();
      // One public snapshot. Ajax owns item fields; GraphQL owns payment details.
      this.state = {
        ...((typeof cart !== "undefined" && cart) || { items: [], item_count: 0 }),
        discountCodes: [],
        discountAllocations: [],
        appliedGiftCards: [],
        summary: null,
      };
      this.revision = 0;
      this.cartUpdateRunning = false;
      this.syncedRevision = -1;
      this.cartMutations = new Set();
      this._cartWriteTail = Promise.resolve();
      this._cartWriteCount = 0;
      this._cartTransaction = null;
      this.cartUpdatePromise = null;
      this.cartUpdateError = null;
      this.cartAbortController = null;
      this._lastUpdateArgs = null;
      this._pendingGiftCardCode = null;
      this._validationInterval = null;
      this._refreshTimeout = null;
      this._optimisticAddId = 0;
      this._connected = false;

      this.requestCartUpdate = this.requestCartUpdate.bind(this);
      this.validateCart = this.validateCart.bind(this);
      this.addAllFromWishlistToCart = this.addAllFromWishlistToCart.bind(this);
      this.addToCart = this.addToCart.bind(this);
      this.showCartError = this.showCartError.bind(this);
      this.removeDiscountCodeFromCart =
        this.removeDiscountCodeFromCart.bind(this);
      this.removeGiftCardFromCart = this.removeGiftCardFromCart.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      window.enrouteCart = this;
      window.__enrouteApp = this;

      if (typeof freeGiftObject !== "undefined" && freeGiftObject.length > 0) {
        window.__enrouteFreeGiftApi = {
          owner: this,
          requestCartUpdate: this.requestCartUpdate,
        };
      }

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("open_bag") && urlParams.get("open_bag")) {
        window.dispatchEvent(new CustomEvent("enroute:cart-popup:open"));
      }
      // Auto-apply a gift card handed off from the gift card page's
      // "USE NOW" button. The raw code is passed via sessionStorage
      // (same origin, same tab) rather than the URL, so this bearer
      // secret never lands in history, the Referer header, or analytics.
      // Shopify's /discount/ permalink cannot apply gift cards, so we
      // route the code here and use the Storefront cart API. Read once
      // and clear. Deferred to afterUpdateTrigger so the cart's computed
      // fields exist.
      let giftCardCode = null;
      try {
        giftCardCode = sessionStorage.getItem("enroute_gift_card_code");
        if (giftCardCode) {
          sessionStorage.removeItem("enroute_gift_card_code");
        }
      } catch (e) {}
      if (giftCardCode) {
        this._pendingGiftCardCode = giftCardCode;
      }
      if (this.state) {
        this._refreshTimeout = setTimeout(() => {
          this.requestCartUpdate();
        }, 1000);
      }

      this._validationInterval = setInterval(() => {
        // Unlike the requestCartUpdate-triggered call (whose caller already
        // refreshed the drawer this tick), nothing else refreshes <cart-items>
        // between ticks here, so force it before validateCart() reads its
        // data-available/data-pre-order attributes off the DOM.
        window._erj_?.cartSections
          ?.refreshSections?.()
          .then(() => this.validateCart());
      }, 60 * 1000);

      // Intentionally do NOT dispatch `enroute:cart-updated` (nor seed
      // __enroutePromoBarState) from the mount payload. `this.state` here is
      // the server-rendered snippets/cart.liquid JSON, whose money fields are
      // `| money` strings (e.g. "$24.00") and which lacks `quantity`
      // (emitted as `q`), `total_discount`, and `final_line_price`. Feeding
      // that partial shape to the promo-bar consumers (free-gift,
      // cart-upsell) makes their numeric math collapse to NaN/0. The first `cart-updated` is instead emitted
      // by requestCartUpdate() above once Ajax and GraphQL have been merged into a numeric snapshot.
    }

    disconnectedCallback() {
      this.invalidate();
      window._erj_?.nativeGiftSelection?.clear();
      if (this._validationInterval) clearInterval(this._validationInterval);
      if (this._refreshTimeout) clearTimeout(this._refreshTimeout);
      this.cartAbortController?.abort();
      this._validationInterval = null;
      this._refreshTimeout = null;
      this._connected = false;
      if (window.enrouteCart === this) window.enrouteCart = null;
      if (window.__enrouteApp === this) window.__enrouteApp = null;
      if (window.__enrouteFreeGiftApi?.owner === this) {
        window.__enrouteFreeGiftApi = null;
      }
    }

    /** One clock for cart writes, state reads and section rendering. */
    invalidate() {
      return ++this.revision;
    }

    isCurrentRevision(revision) {
      return this.isConnected && revision === this.revision;
    }

    /** Queue independent writers; a transaction retains its turn across reads and rollback. */
    _queueCartWrite(operation) {
      this._cartWriteCount++;
      const task = this._cartWriteTail.then(() => {
        if (!this.isConnected) throw new Error("Cart is disconnected");
        return operation();
      }).finally(() => { this._cartWriteCount--; });
      // A failed write must not prevent the next writer from running.
      this._cartWriteTail = task.catch(() => {});
      return task;
    }

    runCartTransaction(operation) {
      return this._queueCartWrite(async () => {
        const transaction = {};
        this._cartTransaction = transaction;
        try { return await operation(transaction); }
        finally { this._cartTransaction = null; }
      });
    }

    /** Only writes carrying the active transaction's explicit token may bypass the queue. */
    trackCartMutation(operation, transaction) {
      if (transaction != null && transaction === this._cartTransaction) {
        return this._trackCartMutation(operation);
      }
      return this._queueCartWrite(() => this._trackCartMutation(operation));
    }

    _trackCartMutation(operation) {
      const revision = this.invalidate();
      const task = Promise.resolve().then(() => operation(revision)).finally(() => {
        this.invalidate();
        this.cartMutations.delete(task);
      });
      this.cartMutations.add(task);
      return task;
    }

    /** Reuse only a snapshot covering all known, completed mutations. */
    async ensureCartFresh(transaction) {
      while (true) {
        if (!this.isConnected) throw new Error("Cart is disconnected");
        const ownsTransaction = transaction != null && transaction === this._cartTransaction;
        if (this._cartWriteCount && !ownsTransaction) {
          await this._cartWriteTail;
          continue;
        }
        if (this.cartMutations.size) {
          await Promise.allSettled([...this.cartMutations]);
          continue;
        }
        if (this.cartUpdateRunning) {
          await this.cartUpdatePromise;
          if (this.cartUpdateError) throw this.cartUpdateError;
          continue;
        }
        if (this.syncedRevision === this.revision) {
          return this.state;
        }
        await this.requestCartUpdate(null, null, { skipSectionRender: true });
        if (this.cartUpdateError) throw this.cartUpdateError;
      }
    }

    /**
     * Refresh the JS-side cart state and re-render the drawer.
     *
     * The drawer itself is server-rendered (snippets/cart-items.liquid,
     * snippets/cart-totals.liquid) and swapped in via the Section Rendering
     * API, so this keeps the unified `this.state` current for its consumers
     * (assets/header.js's badge, CartDiscountForm, the promo bar and
     * free-gift components) and broadcasts `enroute:cart-updated`.
     *
     * @param {?Array} items - Unused by the refresh itself; kept because
     *   callers still pass the items a mutation returned.
     * @param {?Object} cart - An Ajax cart payload to reuse instead of
     *   refetching /cart.js.
     * @param {{skipSectionRender?: boolean, offerSwitch?: boolean}} [options] - Set by
     *   assets/global.js when the caller already swapped the
     *   drawer fragments in from its own mutation response.
     */
    requestCartUpdate(items, cart, options) {
      this.invalidate();
      window._erj_?.nativeGiftSelection?.clear({ preservePending: true });
      this._lastUpdateArgs = { items, cart, options };

      if (this.cartUpdateRunning) {
        // Stop the obsolete read; the shared promise includes its replacement.
        this.cartAbortController?.abort();
        return this.cartUpdatePromise;
      }

      this.cartUpdateRunning = true;
      let revision = this.revision;
      const request = this._lastUpdateArgs;
      this.cartUpdateError = null;
      const skipSectionRender = Boolean(options && options.skipSectionRender);

      this.cartUpdatePromise = (async () => {
        // Mutation success callbacks can schedule this refresh before the tracking
        // promise settles. Read only after all pending writes have completed.
        let waitedForMutation = false;
        while (this.cartMutations.size) {
          waitedForMutation = true;
          await Promise.allSettled([...this.cartMutations]);
          if (!this.isConnected || request !== this._lastUpdateArgs) return;
        }
        if (waitedForMutation) cart = null;
        // Writes may have finished while we waited; all reads below share this revision.
        revision = this.revision;
        // Each read round owns its abort controller.
        this.cartAbortController?.abort();
        this.cartAbortController = new AbortController();
        const { signal } = this.cartAbortController;
        if (!cart) {
          const response = await fetch(window.Shopify.routes.root + "cart.js", {
            signal,
            cache: "no-store",
          });
          if (!response.ok) throw new Error("Network response was not ok");
          cart = await response.json();
        }

        // Work on a new object so reused Ajax payloads cannot mutate published state.
        const { discount_codes: _ajaxDiscountCodes, ...cartData } = cart;

        // Per-line-item money formatting used to happen here for the Vue
        // template; the drawer now gets those strings from Liquid's | money.
        // What remains are the cart-level fields consumers still read off
        // `this.state`: `total` (global.js's estimated-total fallback) and
        // the discount aggregations (CartDiscountForm's disabled-state check
        // and global.js's discount rows).
        cartData.total = Shopify.formatMoney(
          cartData.total_price,
          Shopify.money_format,
        ).replace(/<[^>]*>?/gm, "");

        cartData.product_level_discount_applications = Object.values(
          cartData.items.reduce((acc, item) => {
            item.line_level_discount_allocations.forEach((allocation) => {
              const da = allocation.discount_application;
              if (da.target_type === "line_item") {
                if (!acc[da.title])
                  acc[da.title] = { title: da.title, amount: 0 };
                acc[da.title].amount += allocation.amount;
              }
            });
            return acc;
          }, {}),
        );
        cartData.product_level_discount_applications_total =
          cartData.product_level_discount_applications.reduce(
            (total, d) => total + d.amount,
            0,
          );

        // The old loop's other per-item fetches — the product's options and
        // variants for the switcher, the ?view=metajson stock text, and the
        // custom.product_types metafield free-gift.js's getEligibleCartCount
        // needs — are now all rendered server-side by snippets/cart-items.liquid
        // (product_types as data-product-types, read straight off the DOM), so
        // no request-per-line-item loop is needed here at all.
        if (!this.isCurrentRevision(revision)) return;

        // Re-render the drawer from the server. Skipped when the caller already
        // swapped the fragments in from its own mutation response.
        if (!skipSectionRender) {
          await window._erj_?.cartSections?.refreshSections({ revision });
        }

        if (!this.isCurrentRevision(revision)) return;

        {
          const cartId = `gid://shopify/Cart/${cartData.token}`;
          const query5 = `
                      query {
                        cart(id: "${cartId}") {
                          id
                          deliveryGroups(first: 20) {
                            nodes {
                              selectedDeliveryOption {
                                deliveryMethodType
                                code
                                title
                                estimatedCost {
                                  amount
                                  currencyCode
                                }
                              }
                            }
                          }
                          estimatedCost {
                            totalAmount{
                              amount
                              currencyCode
                            }
                            subtotalAmount{
                              amount
                              currencyCode
                            }
                            checkoutChargeAmount{
                              amount
                              currencyCode
                            }
                            totalDutyAmount{
                              amount
                              currencyCode
                            }
                          }
                          appliedGiftCards {
                            amountUsed {
                              amount
                              currencyCode
                            }
                            balance {
                              amount
                              currencyCode
                            }
                            id
                            lastCharacters
                            presentmentAmountUsed {
                              amount
                              currencyCode
                            }
                          }
                          discountAllocations{
                            discountedAmount{
                              amount
                              currencyCode
                            }
                          }
                          discountCodes{
                            applicable
                            code
                          }
                          lines(first: 200){
                            nodes{
                              id
                              quantity
                            }
                          }
                        }
                      }`;
          await fetch("/api/2024-10/graphql.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                window.EnrouteStorefront.token,
            },
            signal,
            body: JSON.stringify({
              query: query5,
            }),
          })
            .then((response) => {
              if (response.ok === false) throw new Error("Cart details request failed");
              return response.json();
            })
            .then((json) => {
              if (!this.isCurrentRevision(revision)) return;
              if (json.errors?.length) {
                const error = new Error(
                  `Cart details request failed: ${json.errors.map((entry) => entry.message).join("; ")}`,
                );
                error.name = "CartDetailsError";
                throw error;
              }
              if (json.data?.cart === null && cartData.items.length === 0) {
                // An empty Ajax cart may not have a Storefront record yet.
                // Clear payment details rather than retaining the previous cart's.
                json.data.cart = {
                  estimatedCost: {},
                  deliveryGroups: { nodes: [] },
                  discountCodes: [],
                  discountAllocations: [],
                  appliedGiftCards: [],
                };
              }
              if (!json.data?.cart) {
                throw new Error("Cart details are unavailable: Storefront returned no cart");
              }
              const summary = { ...json.data.cart.estimatedCost };
              if (json.data.cart !== null) {
                if (summary.checkoutChargeAmount) {
                  summary.checkoutChargeAmount.formated =
                    Shopify.formatMoney(
                      summary.checkoutChargeAmount.amount *
                        100.0,
                      Shopify.money_format,
                    ).replace(/<[^>]*>?/gm, "");
                }
                if (summary.subtotalAmount) {
                  summary.subtotalAmount.formated =
                    Shopify.formatMoney(
                      summary.subtotalAmount.amount *
                        100.0,
                      Shopify.money_format,
                    ).replace(/<[^>]*>?/gm, "");
                }
                if (summary.totalAmount) {
                  summary.totalAmount.formated =
                    Shopify.formatMoney(
                      summary.totalAmount.amount * 100.0,
                      Shopify.money_format,
                    ).replace(/<[^>]*>?/gm, "");
                }
                if (
                  summary.subtotalAmount &&
                  summary.totalAmount
                ) {
                  summary.discount =
                    (summary.subtotalAmount.amount -
                      summary.totalAmount.amount) *
                    100.0;
                  summary.discount_formated =
                    Shopify.formatMoney(
                      summary.discount,
                      Shopify.money_format,
                    ).replace(/<[^>]*>?/gm, "");
                }

                summary.discounts = json.data.cart.discountAllocations.reduce(
                  (sum, discount) => {
                    return sum + parseFloat(discount.discountedAmount.amount);
                  },
                  0,
                );
                summary.discounts_formated = Shopify.formatMoney(
                  summary.discounts * 100.0,
                  Shopify.money_format,
                ).replace(/<[^>]*>?/gm, "");
                summary.giftCards = json.data.cart.appliedGiftCards.reduce(
                  (sum, giftCard) => {
                    return (
                      sum + parseFloat(giftCard.presentmentAmountUsed.amount)
                    );
                  },
                  0,
                );
                summary.giftCards_formated = Shopify.formatMoney(
                  summary.giftCards * 100.0,
                  Shopify.money_format,
                ).replace(/<[^>]*>?/gm, "");
                summary.general = summary.discounts + summary.giftCards;
                summary.general_formated = Shopify.formatMoney(
                  summary.general * 100.0,
                  Shopify.money_format,
                ).replace(/<[^>]*>?/gm, "");
                summary.shipping = false;

                if (
                  json.data.cart.deliveryGroups.nodes.length > 0 &&
                  json.data.cart.deliveryGroups.nodes[0]
                    .selectedDeliveryOption &&
                  json.data.cart.deliveryGroups.nodes[0].selectedDeliveryOption
                    .estimatedCost
                ) {
                  summary.shipping = true;
                  summary.shippingTitle =
                    json.data.cart.deliveryGroups.nodes[0].selectedDeliveryOption.title;
                  summary.shippingCost =
                    json.data.cart.deliveryGroups.nodes[0].selectedDeliveryOption.estimatedCost.amount;
                  summary.shippingCost_formated = Shopify.formatMoney(
                    summary.shippingCost * 100.0,
                    Shopify.money_format,
                  ).replace(/<[^>]*>?/gm, "");
                }
              }

              const details = json.data.cart;
              Object.assign(cartData, {
                discountCodes: details.discountCodes || [],
                discountAllocations: details.discountAllocations || [],
                appliedGiftCards: details.appliedGiftCards || [],
                summary,
              });
            });
        }
        // Ajax, GraphQL and sections all belong to the same revision.
        if (!this.isCurrentRevision(revision)) return;
        this.state = cartData;
        this.syncedRevision = revision;
        this.validateCart();

        // Apply a gift card requested via URL once the cart is fully
        // populated. Clear the flag before applying so the follow-up
        // cart update triggered by addGiftCardToCart doesn't re-run it.
        if (this._pendingGiftCardCode) {
          const pendingCode = this._pendingGiftCardCode;
          this._pendingGiftCardCode = null;
          window.dispatchEvent(
            new CustomEvent("enroute:apply-gift-card", {
              detail: { code: pendingCode },
            }),
          );
        }

        if (this.state) {
          window.__enroutePromoBarState = { cart: this.state };
          window.dispatchEvent(
            new CustomEvent("enroute:cart-updated", {
              detail: { cart: this.state, state: this.state, offerSwitch: Boolean(options?.offerSwitch) },
            }),
          );
        }
      })()
        .catch((error) => {
          if (!this.isCurrentRevision(revision)) return;
          this.syncedRevision = -1;
          this.cartUpdateError = error;
          if (error.name !== "AbortError") console.error(error);
        })
        .finally(() => {
          this.cartUpdateRunning = false;
          if (this.isConnected && revision !== this.revision) {
            // A newer request or write invalidated this round. Read from the server;
            // neither a supplied Ajax payload nor previously rendered HTML is safe to reuse.
            const args = this._lastUpdateArgs || {};
            return this.requestCartUpdate(null, null, {
              ...args.options,
              skipSectionRender: false,
            });
          }
        });
      return this.cartUpdatePromise;
    }
    /**
     * Read each cart line's live available/pre-order state straight off the
     * `<cart-items>` DOM (rendered server-side by snippets/cart-items.liquid),
     * keyed by line `key`. Replaces the old per-handle products/{handle}.js
     * fetch loop — the drawer already carries this data on every render.
     * Returns null if the drawer isn't mounted on this page.
     */
    getFreshLineItemData() {
      const rows = document.querySelectorAll("cart-items [data-cart-item]");
      if (rows.length === 0) return null;
      const map = new Map();
      rows.forEach((row) => {
        const key = row.dataset.lineKey;
        if (!key) return;
        map.set(key, {
          available: row.dataset.available === "true",
          preOrder: row.dataset.preOrder === "true",
          preOrderShipDate: row.dataset.preOrderShipDate || null,
          preOrderShipDateYmd: row.dataset.preOrderShipDateYmd || null,
        });
      });
      return map;
    }
    async validateCart() {
      const freshByKey = this.getFreshLineItemData();
      if (!freshByKey) return;

      try {
        function addOrInc(array, newItem) {
          function getRelevantProps(props = {}) {
            return {
              gift: props.gift,
              customerFile: props["Customer File"],
              customLetters: props["Custom letters"],
              customTags: props["Custom Tags"],
            };
          }

          const newProps = getRelevantProps(newItem.properties);
          const existing = array.find((item) => {
            if (item.id !== newItem.id) return false;
            const itemProps = getRelevantProps(item.properties);
            return JSON.stringify(itemProps) === JSON.stringify(newProps);
          });

          if (existing) {
            existing.quantity += newItem.quantity;
          } else {
            array.push({
              id: newItem.id,
              quantity: newItem.quantity,
              properties: newItem.properties,
            });
          }
        }

        const keysToRemove = new Set();
        const toAdd = [];

        this.state.items.forEach((cartItem) => {
          const val = freshByKey.get(cartItem.key);
          if (val && !val.available) {
            keysToRemove.add(cartItem.key);
          }
        });

        this.state.items.forEach((cartItem) => {
          if (keysToRemove.has(cartItem.key)) return;
          const val = freshByKey.get(cartItem.key);
          if (!val) return;

          const isPreOrder = !!(val.preOrder && val.preOrderShipDate);
          const cartStrings = window._erj_?.getGlobalLocaleStrings?.()?.cart;
          const shippedByLabel = cartStrings?.shipped_by || "Shipped by";
          // Every language's "Shipped by" label, so stale keys left behind by an
          // earlier language selection can be purged (keep only the current one).
          const shippedByVariants = new Set([
            shippedByLabel,
            ...(cartStrings?.shipped_by_variants || []),
          ]);

          const newShippedBy = isPreOrder ? val.preOrderShipDate : null;
          const oldShippedBy = cartItem.properties[shippedByLabel] ?? null;
          // Language-independent YYYY/MM/DD ship date, kept in the hidden `_pre_order` property.
          const newPreOrder = isPreOrder ? (val.preOrderShipDateYmd ?? null) : null;
          const oldPreOrder = cartItem.properties["_pre_order"] ?? null;
          // A shipped-by key in any language other than the current one is stale
          // (e.g. added in EN, now browsing DE) and must be cleaned up.
          const hasStaleShippedBy = Object.keys(cartItem.properties).some(
            (key) => key !== shippedByLabel && shippedByVariants.has(key),
          );

          if (
            newShippedBy !== oldShippedBy ||
            newPreOrder !== oldPreOrder ||
            hasStaleShippedBy
          ) {
            let properties = Object.assign({}, cartItem.properties);
            // Drop every language's shipped-by label + the hidden marker, then re-add
            // only the current language with the latest ship date.
            shippedByVariants.forEach((key) => delete properties[key]);
            delete properties["_pre_order"];
            if (newShippedBy) properties[shippedByLabel] = newShippedBy;
            if (newPreOrder) properties["_pre_order"] = newPreOrder;
            addOrInc(toAdd, {
              id: cartItem.id,
              quantity: cartItem.quantity,
              properties,
            });
            keysToRemove.add(cartItem.key);
          }
        });

        let needViewRefresh = false;
        if (keysToRemove.size > 0) {
          needViewRefresh = true;

          const keys = [...keysToRemove];
          const updatesObject = {};
          keys.forEach((key) => {
            updatesObject[key] = 0;
          });
          //console.log("request update object", updatesObject);
          const response = await window._erj_.fetchCartMutation(window._erj_.localePath("cart/update.js"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates: updatesObject }),
          });
          //console.log(response);
          if (!response.ok) {
            throw new Error(`Błąd zapytania: HTTP ${response.status}`);
          }

          const data = await response.json();
          //console.log('products deleted:', data);
        }
        if (toAdd.length > 0) {
          //add procedure
          const addRes = await window._erj_.fetchCartMutation(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: toAdd }),
            },
          );
          if (!addRes.ok) {
            throw new Error(`Błąd zapytania: HTTP ${response.status}`);
          }
        }

        if (needViewRefresh) {
          this.requestCartUpdate();
        }
      } catch (error) {
        console.error("Wystąpił błąd przy aktualizacji koszyka:", error);
      }
    }
    addAllFromWishlistToCart(wishlist) {
      window.dispatchEvent(
        new CustomEvent("enroute:cart-popup:open", {
          detail: { recalculate: false },
        }),
      );
      let items = [];
      wishlist.wishlistItems.forEach((item) => {
        if (item.exists && item.available) {
          items.push({ id: item.variantId, quantity: item.quantity });
        }
      });

      const count = this.state?.items?.length ?? 0;

      const limit = 70;
      const availableSlots = Math.max(limit - count, 0);

      if (items.length > availableSlots) {
        console.warn(
          `Im cutting wishlist ${availableSlots} from ${items.length} to not exceed ${limit}`,
        );
        items = items.slice(0, availableSlots);
      }

      if (items.length == 0) return;

      this.addToCart({ items });
    }
    async addToCart(e) {
      //console.log(this.state.items);
      if (window.screen.width > 765) {
        document.getElementById("cart")?.open(false);
      }

      const SUCCESS_TIMEOUT = 3000; // 3 seconds

      let itemsToAdd = [];
      if (e.items && Array.isArray(e.items)) {
        itemsToAdd = e.items;
      } else {
        itemsToAdd = [e];
      }

      const currentTotalQty = (this.state?.items || []).reduce(
        (sum, item) => sum + (item.quantity || 0),
        0,
      );
      const incomingTotalQty = itemsToAdd.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0,
      );

      if (currentTotalQty + incomingTotalQty > 70) {
        document
          .querySelector("add-success-toast")
          ?.show("\n\nYour bag is full", 3000);
        const items = this.state?.items ?? [];
        if (items.length > 70) {
          const toRemoveCount = items.length - 70;
          const nonGifts = items.filter(
            (i) => i?.properties?._gift !== "free_gift",
          );
          const toRemove = nonGifts.slice(-toRemoveCount);
          const payload = toRemove.map((i) => ({ id: i.key, quantity: 0 }));

          console.warn(
            `Reducing ${items.length} items — deleting ${toRemoveCount} (excluding free_gifts)`,
          );
          console.table(payload);
          window._erj_.fetchCartMutation(`${window.Shopify.routes.root}cart/update.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              updates: Object.fromEntries(payload.map((p) => [p.id, 0])),
            }),
          })
            .then((response) => response.json())
            .then((data) => {
              console.log("Cart updated:", data);
              // cart/update.js already returns the full cart — reuse it instead
              // of making requestCartUpdate fetch cart.js all over again.
              this.requestCartUpdate(null, data);
            })
            .catch((err) => {
              console.error("Error while reducing:", err);
            });
        }
      } else {
        let totalQtyToAdd = itemsToAdd.reduce(
          (sum, item) => sum + (Number(item.quantity) || 1),
          0,
        );
        const optimisticId = ++this._optimisticAddId;
        const optimisticCart = this.state;
        try {
          this.state.item_count += totalQtyToAdd;
          window.__enroutePromoBarState = { cart: this.state };
          window.dispatchEvent(
            new CustomEvent("enroute:cart-updated", {
              detail: {
                cart: this.state,
                state: this.state,
                optimistic: true,
                optimisticId,
                items: itemsToAdd,
              },
            }),
          );
          document
            .querySelector("add-success-toast")
            ?.show("", SUCCESS_TIMEOUT);
          const cart = document.getElementById("cart");
          const isBagMode = cart?.classList.contains("active") && cart.dataset.mode == "bag";
          if (!isBagMode)
            document.getElementById("mobileconfirmationtoast")?.show();
          let newItemsObject = await (async () => {
            console.log("adding", itemsToAdd);
            const items = itemsToAdd.map(({ id, quantity, properties }) => ({
              id,
              quantity: quantity || 1,
              ...(properties &&
                Object.keys(properties).length > 0 && { properties }),
            }));
            const response = await window._erj_.fetchCartMutation(
              `${window.Shopify.routes.root}cart/add.js`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  items: items,
                }),
              },
            );
            const data = await response.json().catch(() => null);
            if (!response.ok) {
              const msg =
                (data && (data.description || data.message)) ||
                `HTTP error! status: ${response.status}`;
              throw new Error(msg);
            }
            return data;
          })();
          if (newItemsObject) {
            this.requestCartUpdate(newItemsObject.items);
          }
        } catch (err) {
          this.showCartError(err && err.message);
          // requestCartUpdate replaces this.state wholesale. If another request
          // committed authoritative state while this add was in flight, that
          // new cart already excludes the rejected add and must not be decremented.
          if (this.state === optimisticCart) {
            this.state.item_count -= totalQtyToAdd;
          }
          window.__enroutePromoBarState = { cart: this.state };
          window.dispatchEvent(
            new CustomEvent("enroute:cart-updated", {
              detail: {
                cart: this.state,
                state: this.state,
                optimistic: true,
                rollback: true,
                optimisticId,
                items: itemsToAdd,
              },
            }),
          );
          console.error(err);
          if (window.screen.width > 765) {
            setTimeout(() => {
              document.getElementById("cart")?.toggle(false);
            }, 2500);
          }
        }
      }
    }
    showCartError(message) {
      const text =
        "\n\n" + (message || "Error while processing. \nTry again later.");
      // If the mobile confirmation toast is already on screen, just swap its
      // text instead of stacking the centered toast on top of it.
      const mobileToast = document.getElementById("mobileconfirmationtoast");
      const mobileToastVisible =
        mobileToast && mobileToast.classList.contains("active");
      if (!mobileToastVisible) {
        // { error: true } forces the toast visible on all viewports (the success
        // .active rule is gated to desktop only).
        document
          .querySelector("add-success-toast")
          ?.show(text, 3000, { error: true });
      }
      mobileToast?.swapText(text);
    }
    removeDiscountCodeFromCart(discountCode) {
      const codes = this.state.discountCodes
        .filter((discount) => discount.applicable && discount.code.toLowerCase() !== discountCode.toLowerCase())
        .map((discount) => discount.code);

      const cartId = `gid://shopify/Cart/${this.state.token}`;
      const query = `
                    mutation {
                      cartDiscountCodesUpdate(cartId: "${cartId}", discountCodes: ${JSON.stringify(codes)}) {
                        cart {
                          id
                          discountCodes {
                            applicable
                            code
                          }
                        }
                        userErrors {
                          field
                          message
                        }
                      }
                    }`;

      window._erj_.fetchGraphQL(query, this.requestCartUpdate);
    }
    removeGiftCardFromCart(id) {
      const cartId = `gid://shopify/Cart/${this.state.token}`;

      const query = `
                    mutation {
                      cartGiftCardCodesUpdate(cartId:  "${cartId}", giftCardCodes: []) {
                        cart{
                          id
                          appliedGiftCards {
                            lastCharacters
                          }
                        }
                        userErrors {
                          field
                          message
                        }
                      }
                    }`;
      window._erj_.fetchGraphQL(query, this.requestCartUpdate);
    }
  }

  window.EnrouteCart = Cart;
  customElements.define("enroute-cart", Cart);
})();
